// Onglet « Suivi » des tâches : taux de réalisation par semaine, par tâche, par employé.
import { useEffect, useState } from 'react'
import { format, subDays } from 'date-fns'
import api from '../lib/api'

interface Stats {
  from: string; to: string
  total: { due: number; done: number; rate: number | null }
  perTask: { id: string; nom: string; zone: string; frequence: string; due: number; done: number; rate: number | null; lastDone: string | null }[]
  perEmployee: { name: string; count: number; photos: number }[]
  perWeek: { week: string; due: number; done: number; rate: number | null }[]
  neverDone: string[]
}

const FREQ: Record<string, string> = { jour: 'Quotidien', semaine: 'Hebdo', mois: 'Mensuel', semestre: 'Semestriel', annee: 'Annuel', exceptionnelle: 'Exceptionnel' }
const rateColor = (r: number | null) => r === null ? 'text-gray-400' : r >= 80 ? 'text-green-600' : r >= 50 ? 'text-amber-600' : 'text-red-600'
const barColor = (r: number | null) => r === null ? 'bg-gray-200' : r >= 80 ? 'bg-green-500' : r >= 50 ? 'bg-amber-500' : 'bg-red-500'

export default function TasksStats({ establishmentId }: { establishmentId: string }) {
  const [weeks, setWeeks] = useState(4)
  const [data, setData] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!establishmentId) return
    const to = format(new Date(), 'yyyy-MM-dd')
    const from = format(subDays(new Date(), weeks * 7 - 1), 'yyyy-MM-dd')
    setLoading(true)
    api.get(`/tasks/stats?establishmentId=${establishmentId}&from=${from}&to=${to}`)
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false))
  }, [establishmentId, weeks])

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        {[4, 8, 12].map(w => (
          <button key={w} onClick={() => setWeeks(w)}
            className={`text-sm px-3 py-1.5 rounded-lg border ${weeks === w ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600'}`}>
            {w} semaines
          </button>
        ))}
        {loading && <span className="text-xs text-gray-400">Calcul…</span>}
      </div>

      {data && <>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border p-4">
            <p className="text-xs text-gray-500">Réalisé</p>
            <p className={`text-2xl font-bold ${rateColor(data.total.rate)}`}>{data.total.rate ?? '—'}%</p>
            <p className="text-xs text-gray-400">{data.total.done} / {data.total.due} tâches dues</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-xs text-gray-500">Jamais faites (depuis toujours)</p>
            <p className={`text-2xl font-bold ${data.neverDone.length ? 'text-red-600' : 'text-green-600'}`}>{data.neverDone.length}</p>
            <p className="text-xs text-gray-400 truncate" title={data.neverDone.join(', ')}>{data.neverDone.slice(0, 3).join(', ')}{data.neverDone.length > 3 ? '…' : ''}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-xs text-gray-500">Validations avec photo</p>
            <p className="text-2xl font-bold text-gray-900">{data.perEmployee.reduce((a, e) => a + e.photos, 0)}</p>
            <p className="text-xs text-gray-400">sur {data.perEmployee.reduce((a, e) => a + e.count, 0)} validations</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Par semaine</p>
          <div className="space-y-2">
            {data.perWeek.map(w => (
              <div key={w.week} className="flex items-center gap-3 text-sm">
                <span className="w-20 text-gray-500">{w.week.replace('-S', ' · S')}</span>
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${barColor(w.rate)}`} style={{ width: `${w.rate ?? 0}%` }} />
                </div>
                <span className={`w-24 text-right tabular-nums ${rateColor(w.rate)}`}>{w.rate ?? '—'}% <span className="text-gray-400">({w.done}/{w.due})</span></span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-xl border overflow-hidden">
            <p className="text-sm font-semibold text-gray-700 px-4 pt-4 pb-2">Par tâche (les moins faites d'abord)</p>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Tâche</th>
                  <th className="text-left px-2 py-2">Fréquence</th>
                  <th className="text-right px-2 py-2">Fait</th>
                  <th className="text-right px-4 py-2">Dernière fois</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.perTask.map(t => (
                  <tr key={t.id}>
                    <td className="px-4 py-2"><p className="font-medium text-gray-800">{t.nom}</p><p className="text-xs text-gray-400">{t.zone}</p></td>
                    <td className="px-2 py-2 text-gray-500">{FREQ[t.frequence] || t.frequence}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${rateColor(t.rate)}`}>{t.done}/{t.due}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{t.lastDone ? format(new Date(t.lastDone), 'dd/MM') : <span className="text-red-500">jamais</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white rounded-xl border overflow-hidden h-fit">
            <p className="text-sm font-semibold text-gray-700 px-4 pt-4 pb-2">Par employé</p>
            <table className="w-full text-sm">
              <tbody className="divide-y">
                {data.perEmployee.map(e => (
                  <tr key={e.name}>
                    <td className="px-4 py-2 text-gray-800">{e.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{e.count}{e.photos > 0 && <span className="text-xs text-gray-400"> · {e.photos} 📷</span>}</td>
                  </tr>
                ))}
                {data.perEmployee.length === 0 && <tr><td className="px-4 py-4 text-gray-400 text-center">Aucune validation</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </>}
    </div>
  )
}
